import BigNumber from 'bignumber.js';
import {
  Scrollable,
  OrderStatus,
  OrderSide,
  OrderType,
  OrderExecution,
  OrderExecutionNotification,
  formattedOrderStatus,
  Instrument,
  ZERO,
} from 'core';
import moment from 'moment';

export type OrderResponse = {
  id: string;
  accountId: number;
  symbolCd: string;
  orderTypeEn: OrderType;
  orderSideEn: OrderSide;
  priceRt: number | null;
  stopPriceRt: number | null;
  quantityAmt: string;
  orderStatusEn: OrderStatus;
  note: string | null;
  eventTs: string; // "2020-08-19T04:22:33.936686Z",
  leverage: number | null;
  ocoOrderId: string | null;
  closingPositionFg: boolean | null;
  targetPartyCd: string | null;
  takerExecutions: Array<OrderExecutionResponse>;
};

export type OrderExecutionResponse = {
  orderId: string;
  ocoOrderId: string;
  orderSideEn: OrderSide;
  orderStatusEn: OrderStatus;
  lastPriceRt: number | null;
  averagePriceRt: number;
  lastQuantityAmt: string;
  leavesQuantityAmt: string;
  cumulativeQuantityAmt: string;
  rejectionReason: string | null;
  transactTimeTs: string; // "2020-08-19T04:22:33.936686Z",
};

export interface Order {
  readonly id: string;
  readonly eventTs: string; // in ISO8601
  readonly orderSideEn: OrderSide;
  readonly quantityAmt: BigNumber;
  readonly orderTypeEn: OrderType;
  readonly priceRt: number | null;
  readonly stopPriceRt: number | null;
  readonly note: string | null;
  readonly leverage: number | null;
  readonly instrument: Instrument;
  readonly takerExecutions: Array<OrderExecution>;
  readonly orderTypeDescription: string;
  readonly closingOrder: boolean;

  get orderStatusEn(): OrderStatus;
  get orderStatus(): string;
  get leavesQuantityAmt(): BigNumber | null;
  get averagePriceRt(): number | null;
  get filledQuantityAmt(): BigNumber;
  get isIncomplete(): boolean;
  get showProtectionIcon(): boolean;
  get protectionTooltip(): string | null;
  get ids(): Array<string>;
  get isEditable(): boolean;

  addExecution(execution: OrderExecutionNotification): void;
  markAsCancelled(): void;
  isViewableInChart(instrument: Instrument | null): boolean;
  isOpen(): boolean;
}

/**
 * An order represents an open or historic order that gets displayed to the user.
 */
export class SingleOrder implements Order, Scrollable<string> {
  constructor(
    readonly id: string,
    private readonly _orderStatusEn: OrderStatus,
    readonly eventTs: string, // in ISO8601
    readonly orderSideEn: OrderSide,
    readonly quantityAmt: BigNumber,
    readonly orderTypeEn: OrderType,
    readonly priceRt: number | null,
    readonly stopPriceRt: number | null,
    readonly note: string | null,
    readonly leverage: number | null,
    readonly instrument: Instrument,
    readonly ocoOrderId: string | null,
    readonly closingOrder: boolean,
    private readonly targetPartyCd: string | null,
    readonly takerExecutions = new Array<OrderExecution>()
  ) {}

  // The list of executions can grow
  get orderStatusEn(): OrderStatus {
    const last = this.lastExecution();
    // no need to check the order's status for equality with Cancelled as it will either match the last execution's or it is New
    if (
      last
      && last.orderStatusEn === OrderStatus.Cancelled
      && this.takerExecutions.some((te) => te.orderStatusEn === OrderStatus.PartiallyFilled)
    ) {
      return OrderStatus.PartiallyFilled;
    }
    return (last && last.orderStatusEn) || this._orderStatusEn;
  }

  get orderStatus(): string {
    if (this.orderStatusEn === OrderStatus.Cancelled) {
      return 'Cancelled';
    } else if (this.orderStatusEn === OrderStatus.Filled) {
      return 'Filled';
    } else if (this.orderStatusEn === OrderStatus.PartiallyFilled) {
      return 'Partial Fill';
    } else if (this.orderStatusEn === OrderStatus.New) {
      return 'New';
    } else if (this.orderStatusEn === OrderStatus.Rejected) {
      return 'Rejected';
    } else {
      return '';
    }
  }

  get leavesQuantityAmt(): BigNumber | null {
    const last = this.lastExecution();
    return last ? last.leavesQuantityAmt : this.quantityAmt;
  }

  get averagePriceRt(): number | null {
    const last = this.lastExecution();
    return last ? last.averagePriceRt : null;
  }

  get filledQuantityAmt(): BigNumber {
    const last = this.lastExecution();
    return last ? last.cumulativeQuantityAmt : ZERO;
  }

  get isIncomplete(): boolean {
    const last = this.lastExecution();
    return this.orderStatusEn === OrderStatus.New || last?.orderStatusEn === OrderStatus.PartiallyFilled;
  }

  get showProtectionIcon(): boolean {
    return this.ocoOrderId !== null && this.closingOrder;
  }

  get protectionTooltip(): string | null {
    if (this.showProtectionIcon && this.orderTypeEn === OrderType.Limit && this.closingOrder) {
      return 'OCO take profit component';
    } else if (this.showProtectionIcon && this.orderTypeEn === OrderType.Stop && this.closingOrder) {
      return 'OCO stop loss component';
    } else {
      return null;
    }
  }

  get orderTypeDescription(): string {
    if (!this.closingOrder && !this.ocoOrderId) {
      if (this.orderTypeEn === OrderType.Market && this.targetPartyCd !== null && this.targetPartyCd !== '') {
        return `Market [${this.targetPartyCd}]`;
      } else if (this.orderTypeEn === OrderType.Market) {
        return 'Market';
      } else if (this.orderTypeEn === OrderType.Limit && this.targetPartyCd !== null && this.targetPartyCd !== '') {
        return `Limit [${this.targetPartyCd}]`;
      } else if (this.orderTypeEn === OrderType.Limit) {
        return 'Limit';
      } else if (this.orderTypeEn === OrderType.StopLimit) {
        return 'Stop Limit';
      } else if (this.orderTypeEn === OrderType.Stop) {
        return 'Stop Market';
      }
    } else if (!this.closingOrder && this.ocoOrderId) {
      if (this.orderTypeEn === OrderType.Limit) {
        return 'OCO Limit';
      } else if (this.orderTypeEn === OrderType.Stop) {
        return 'OCO Stop';
      }
    } else if (this.closingOrder && !this.ocoOrderId) {
      if (this.orderTypeEn === OrderType.Market) {
        return 'Market position close';
      } else if (this.orderTypeEn === OrderType.Limit) {
        return 'Limit position close';
      }
    } else if (this.closingOrder && this.ocoOrderId) {
      if (this.orderTypeEn === OrderType.Limit) {
        return 'Position take profit';
      } else if (this.orderTypeEn === OrderType.Stop) {
        return 'Position stop loss';
      }
    }

    return '';
  }

  get ids(): Array<string> {
    if (this.ocoOrderId === null) {
      return [this.id];
    } else {
      return [this.id, this.ocoOrderId];
    }
  }

  get isEditable(): boolean {
    return false;
  }

  isViewableInChart(instrument: Instrument | null): boolean {
    return (
      instrument?.id === this.instrument.id
      && this.orderStatusEn !== OrderStatus.Cancelled
      && this.orderStatusEn !== OrderStatus.Rejected
      && this.chartDisplayPrice !== null
    );
  }

  isOpen(): boolean {
    return (
      this.orderStatusEn === OrderStatus.New
      || (this.orderStatusEn == OrderStatus.PartiallyFilled && this._orderStatusEn !== OrderStatus.Cancelled)
    );
  }

  get chartDisplayPrice(): number | null {
    if (this.averagePriceRt) {
      return this.averagePriceRt;
    } else if (this.priceRt) {
      return this.priceRt;
    } else if (this.stopPriceRt) {
      return this.stopPriceRt;
    } else {
      return null;
    }
  }

  addExecution(execution: OrderExecutionNotification): void {
    this.takerExecutions.push(execution.execution);
    // Transact timestamp are strings like 2021-03-05T09:28:54.073Z where the fractional seconds is variable
    // length, eg. 54.07317Z. To get correct sorting the terminal Z must be ignored.
    this.takerExecutions.sort((a, b) => a.transactTimeTs.slice(0, -1).localeCompare(b.transactTimeTs.slice(0, -1)));
  }

  markAsCancelled(): void {
    const last = this.lastExecution();

    this.addExecution({
      execution: new OrderExecution(
        this.id,
        this.ocoOrderId,
        moment().toISOString(),
        this.orderSideEn,
        ZERO,
        last ? last.cumulativeQuantityAmt : ZERO,
        last ? last.averagePriceRt : 0,
        null,
        OrderStatus.Cancelled,
        null,
        ZERO
      ),
      orderTypeEn: this.orderTypeEn,
      symbolCd: this.instrument.id,
      priceRt: this.priceRt,
      stopPriceRt: this.stopPriceRt,
      clientOrderId: null,
      leverage: this.leverage,
      closingPositionFg: this.closingOrder,
      note: this.note,
      targetPartyCd: this.targetPartyCd,
    });
  }

  private lastExecution(): OrderExecution | undefined {
    // Cancelled is always last so avoid the case where a partial fill and cancel occur in the same instant.
    return this.takerExecutions.find((exec) => exec.orderStatusEn === OrderStatus.Cancelled) || this.takerExecutions.slice(-1)[0];
  }
}

export class OCOOrder implements Order {
  constructor(readonly id: string, private takeProfit: SingleOrder, private stopLoss: SingleOrder) {}

  get takeProfitId(): string {
    return this.takeProfit.id;
  }

  get stopLossId(): string {
    return this.stopLoss.id;
  }

  get eventTs(): string {
    return this.takeProfit.eventTs;
  }

  get orderSideEn(): OrderSide {
    return this.takeProfit.orderSideEn;
  }

  get quantityAmt(): BigNumber {
    return this.takeProfit.quantityAmt;
  }

  get orderTypeEn(): OrderType {
    if (this.takeProfit.filledQuantityAmt.gt(ZERO)) {
      return this.takeProfit.orderTypeEn;
    } else if (this.stopLoss.filledQuantityAmt.gt(ZERO)) {
      return this.stopLoss.orderTypeEn;
    } else {
      return OrderType.OCO;
    }
  }

  get note(): string | null {
    return this.takeProfit.note;
  }

  get priceRt(): number | null {
    return this.takeProfit.priceRt;
  }

  get stopPriceRt(): number | null {
    return this.stopLoss.stopPriceRt;
  }

  get leverage(): number | null {
    return this.takeProfit.leverage;
  }

  get instrument(): Instrument {
    return this.takeProfit.instrument;
  }

  get takerExecutions(): OrderExecution[] {
    return this.takeProfit.takerExecutions;
  }

  get orderStatusEn(): OrderStatus {
    if (this.takeProfit.filledQuantityAmt.gt(ZERO)) {
      return this.takeProfit.orderStatusEn;
    } else {
      return this.takeProfit.orderStatusEn;
    }
  }

  get orderStatus(): string {
    return formattedOrderStatus(this.orderStatusEn);
  }

  get leavesQuantityAmt(): BigNumber | null {
    if (this.takeProfit.filledQuantityAmt.gt(ZERO)) {
      return this.takeProfit.leavesQuantityAmt;
    } else if (this.stopLoss.filledQuantityAmt.gt(ZERO)) {
      return this.stopLoss.leavesQuantityAmt;
    } else {
      return this.takeProfit.leavesQuantityAmt;
    }
  }

  get averagePriceRt(): number | null {
    return this.takeProfit.averagePriceRt;
  }

  get filledQuantityAmt(): BigNumber {
    return this.takeProfit.filledQuantityAmt;
  }

  get isIncomplete(): boolean {
    return this.takeProfit.isIncomplete;
  }

  get closingOrder(): boolean {
    return this.takeProfit.closingOrder;
  }

  get showProtectionIcon(): boolean {
    return this.takeProfit.closingOrder;
  }

  get protectionTooltip(): string | null {
    return this.showProtectionIcon ? 'Take profit/stop loss bracket on a position' : null;
  }

  get orderTypeDescription(): string {
    if (this.takeProfit.closingOrder) {
      return 'TP/SL bracket';
    } else {
      return 'OCO';
    }
  }

  get ids(): Array<string> {
    return [this.takeProfitId, this.stopLossId];
  }

  get isEditable(): boolean {
    return (!this.closingOrder && this.orderStatusEn === OrderStatus.New) || (this.closingOrder && this.filledQuantityAmt.eq(ZERO));
  }

  isViewableInChart(instrument: Instrument | null): boolean {
    return instrument?.id === this.instrument.id && this.orderStatusEn !== OrderStatus.Cancelled;
  }

  isOpen(): boolean {
    return this.takeProfit.isOpen() || this.stopLoss.isOpen();
  }

  addExecution(execution: OrderExecutionNotification): void {
    if (execution.execution.orderId === this.takeProfitId) {
      this.takeProfit.addExecution(execution);
    } else if (execution.execution.orderId === this.stopLossId) {
      this.stopLoss.addExecution(execution);
    }
  }

  markAsCancelled(): void {
    this.takeProfit.markAsCancelled();
    this.stopLoss.markAsCancelled();
  }
}

export const buildSingleOrder = (rawData: OrderResponse, instrument: Instrument): SingleOrder => {
  return new SingleOrder(
    rawData.id,
    rawData.orderStatusEn,
    rawData.eventTs,
    rawData.orderSideEn,
    new BigNumber(rawData.quantityAmt),
    rawData.orderTypeEn,
    rawData.priceRt,
    rawData.stopPriceRt,
    rawData.note,
    rawData.leverage,
    instrument,
    rawData.ocoOrderId,
    rawData.closingPositionFg === true,
    rawData.targetPartyCd,
    rawData.takerExecutions.map((te) => fromExecution(te))
  );
};

export const fromOrders = (rawOrders: Array<OrderResponse>, instrumentsById: Map<string, Instrument>): Array<Order> => {
  const orders = new Array<Order>();

  const ordersById = new Map<string, OrderResponse>(rawOrders.map((o) => [o.id, o]));
  const processedIds = new Set<string>();
  for (let i = rawOrders.length - 1; i >= 0; i--) {
    const rawData = rawOrders[i];
    if (processedIds.has(rawData.id)) {
      // We've already dealt with this one when we processed its partner OCO order.
    } else {
      const instrument = instrumentsById.get(rawData.symbolCd)!;
      const thisOrder = buildSingleOrder(rawData, instrument);

      if (rawData.ocoOrderId === null || !ordersById.has(rawData.ocoOrderId)) {
        orders.unshift(thisOrder);
      } else {
        const otherOrder = buildSingleOrder(ordersById.get(rawData.ocoOrderId)!, instrument);
        if (thisOrder.orderTypeEn === OrderType.Limit) {
          orders.unshift(new OCOOrder(thisOrder.id, thisOrder, otherOrder));
        } else {
          orders.unshift(new OCOOrder(thisOrder.id, otherOrder, thisOrder));
        }

        processedIds.add(rawData.ocoOrderId);
      }
    }
  }

  return orders;
};

/* Only supports notifications of *NEW* orders */
export const orderFromNotification = (execution: OrderExecutionNotification, instrument: Instrument): SingleOrder => {
  return new SingleOrder(
    execution.execution.orderId,
    execution.execution.orderStatusEn,
    execution.execution.transactTimeTs,
    execution.execution.orderSideEn,
    execution.execution.leavesQuantityAmt ?? ZERO,
    execution.orderTypeEn,
    execution.priceRt ? execution.priceRt : null,
    execution.stopPriceRt ? execution.stopPriceRt : null,
    execution.note,
    execution.leverage,
    instrument,
    execution.execution.ocoOrderId,
    execution.closingPositionFg === true,
    execution.targetPartyCd,
    []
  );
};

export const fromExecution = (rawData: OrderExecutionResponse) =>
  new OrderExecution(
    rawData.orderId,
    rawData.ocoOrderId,
    rawData.transactTimeTs,
    rawData.orderSideEn,
    new BigNumber(rawData.lastQuantityAmt),
    new BigNumber(rawData.cumulativeQuantityAmt),
    rawData.averagePriceRt,
    rawData.lastPriceRt,
    rawData.orderStatusEn,
    rawData.rejectionReason,
    new BigNumber(rawData.leavesQuantityAmt)
  );

export class OrderTableList {
  public Date: string;
  public Market: string;
  public Type: string;
  public Volume: number | string;
  public PendingVolume: number | string;
  public RequestedPrice?: number | string | null;
  public ActualPrice: number | string;
  public Description: string;
  public Status: string;
  public Note: string | null;
}
